import { shadows } from '@alison/ui';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Popover from '@mui/material/Popover';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { useTRPC } from '@/lib/trpc.js';
import { NetworkBadge } from './NetworkBadge.js';

interface AccountPickerProps {
  selectedIntegrationIds: number[];
  onChange: (integrationIds: number[]) => void;
}

interface ProductGroup {
  productId: number;
  productName: string;
  clientId: number;
  clientName: string;
  integrations: { id: number; name: string; networkName: string; networkLogo: string }[];
}

interface ClientGroup {
  clientId: number;
  clientName: string;
  products: ProductGroup[];
  integrationIds: number[];
}

const triggerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  height: 36,
  px: 2,
  border: '1.5px solid',
  borderRadius: 1,
  bgcolor: 'background.paper',
  cursor: 'pointer',
};

const checkboxSx: SxProps<Theme> = {
  p: 0,
  mr: 1,
  color: 'border.strong',
  '&.Mui-checked': { color: 'primary.main' },
  '&.MuiCheckbox-indeterminate': { color: 'primary.main' },
  '& .MuiSvgIcon-root': { fontSize: 18 },
};

const clientHeaderSx: SxProps<Theme> = {
  m: 0,
  width: '100%',
  px: 2,
  py: 2,
  bgcolor: 'background.page',
  '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 },
};

const accountRowSx: SxProps<Theme> = {
  m: 0,
  width: '100%',
  px: 1,
  py: 1,
  borderRadius: 1,
  '&:hover': { bgcolor: 'background.page' },
  '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 },
};

function groupByClient(productGroups: ProductGroup[]): ClientGroup[] {
  const byClient = new Map<number, ClientGroup>();
  for (const productGroup of productGroups) {
    let clientGroup = byClient.get(productGroup.clientId);
    if (!clientGroup) {
      clientGroup = {
        clientId: productGroup.clientId,
        clientName: productGroup.clientName,
        products: [],
        integrationIds: [],
      };
      byClient.set(productGroup.clientId, clientGroup);
    }
    clientGroup.products.push(productGroup);
    for (const integration of productGroup.integrations) {
      clientGroup.integrationIds.push(integration.id);
    }
  }
  return Array.from(byClient.values());
}

export function AccountPicker({ selectedIntegrationIds, onChange }: AccountPickerProps): ReactNode {
  const trpc = useTRPC();
  const selectableIntegrationsQuery = useQuery(trpc.users.selectableIntegrations.queryOptions());
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = anchorEl !== null;

  function toggleIntegration(integrationId: number): void {
    const isSelected = selectedIntegrationIds.includes(integrationId);
    onChange(
      isSelected
        ? selectedIntegrationIds.filter((id) => id !== integrationId)
        : [...selectedIntegrationIds, integrationId],
    );
  }

  function toggleClient(integrationIds: number[], allSelected: boolean): void {
    if (allSelected) {
      onChange(selectedIntegrationIds.filter((id) => !integrationIds.includes(id)));
    } else {
      onChange(Array.from(new Set([...selectedIntegrationIds, ...integrationIds])));
    }
  }

  if (selectableIntegrationsQuery.isPending) {
    return (
      <Box sx={{ ...triggerSx, borderColor: 'divider', justifyContent: 'center' }}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  const clientGroups = groupByClient(selectableIntegrationsQuery.data ?? []);
  const selectedCount = selectedIntegrationIds.length;
  const summary =
    selectedCount === 0
      ? 'Select integrations'
      : `${selectedCount} integration${selectedCount === 1 ? '' : 's'} selected`;

  return (
    <>
      <Box
        role="button"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        data-testid="button-account-picker"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setAnchorEl(event.currentTarget);
          }
        }}
        sx={{
          ...triggerSx,
          borderColor: isOpen ? 'primary.main' : 'divider',
          '&:hover': { borderColor: 'primary.main' },
        }}
      >
        <Typography
          sx={{
            flex: 1,
            fontSize: 13,
            color: selectedCount === 0 ? 'text.secondary' : 'text.primary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {summary}
        </Typography>
        <KeyboardArrowDownIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
      </Box>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              width: anchorEl?.offsetWidth,
              mt: 1,
              maxHeight: 320,
              overflowY: 'auto',
              overflowX: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              boxShadow: shadows.card,
            },
          },
        }}
      >
        <Box data-testid="list-account-picker">
          {clientGroups.length === 0 ? (
            <Typography sx={{ px: 2, py: 3, fontSize: 13, color: 'text.secondary' }}>
              No integrations available.
            </Typography>
          ) : (
            clientGroups.map((clientGroup, clientIndex) => {
              const selectedInClient = clientGroup.integrationIds.filter((id) =>
                selectedIntegrationIds.includes(id),
              ).length;
              const allSelected =
                clientGroup.integrationIds.length > 0 &&
                selectedInClient === clientGroup.integrationIds.length;
              const someSelected = selectedInClient > 0 && !allSelected;

              return (
                <Box
                  key={clientGroup.clientId}
                  sx={{
                    borderTop: clientIndex === 0 ? 'none' : '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <FormControlLabel
                    data-testid={`checkbox-client-${clientGroup.clientId}`}
                    sx={clientHeaderSx}
                    control={
                      <Checkbox
                        size="small"
                        disableRipple
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={() => toggleClient(clientGroup.integrationIds, allSelected)}
                        sx={checkboxSx}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>
                          {clientGroup.clientName}
                        </Typography>
                        <Typography sx={{ ml: 'auto', fontSize: 11, color: 'text.disabled' }}>
                          {selectedInClient}/{clientGroup.integrationIds.length}
                        </Typography>
                      </Box>
                    }
                  />
                  <Box sx={{ px: 1, pb: 2 }}>
                    {clientGroup.products.map((productGroup) => (
                      <Box key={productGroup.productId} sx={{ mt: 1 }}>
                        <Typography
                          sx={{
                            display: 'block',
                            pl: 4,
                            mb: 1,
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            color: 'text.disabled',
                          }}
                        >
                          {productGroup.productName}
                        </Typography>
                        {productGroup.integrations.map((integration) => (
                          <FormControlLabel
                            key={integration.id}
                            data-testid={`checkbox-integration-${integration.id}`}
                            sx={accountRowSx}
                            control={
                              <Checkbox
                                size="small"
                                disableRipple
                                checked={selectedIntegrationIds.includes(integration.id)}
                                onChange={() => toggleIntegration(integration.id)}
                                sx={{ ...checkboxSx, ml: 2 }}
                              />
                            }
                            label={
                              <Box
                                sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}
                              >
                                <NetworkBadge
                                  networkName={integration.networkName}
                                  networkLogo={integration.networkLogo}
                                />
                                <Typography
                                  sx={{
                                    fontSize: 13,
                                    color: 'text.primary',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {integration.name}
                                </Typography>
                              </Box>
                            }
                          />
                        ))}
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Popover>
    </>
  );
}
